import {
  Autocomplete,
  Button,
  TextField,
  Typography,
  Box,
  alpha,
  useTheme,
  Divider,
  Stack,
  Chip,
  InputAdornment,
} from "@mui/material";
import { useState, useMemo } from "react";
import { styled } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { LabelOutlined, Add } from "@mui/icons-material";
import { useQueryClient } from "@tanstack/react-query";
import DialogBase from "./DialogBase";
import { UtilityService } from "../../services/UtilityService";
import { LabelsService } from "../../services/LabelsService";
import { useDevices } from "../../hooks/queries/useDevices";
import useLabels, { labelKeys, useLabelDevices } from "../../hooks/useLabels";

const PRESET_COLORS = [
  "#EF4444",
  "#F97316",
  "#F59E0B",
  "#22C55E",
  "#10B981",
  "#14B8A6",
  "#06B6D4",
  "#3B82F6",
  "#6366F1",
  "#8B5CF6",
  "#A855F7",
  "#EC4899",
  "#F43F5E",
  "#78716C",
  "#64748B",
  "#0EA5E9",
];

const StyledCancelButton = styled(Button)(({ theme }) => ({
  borderRadius: "8px",
  textTransform: "none",
  fontWeight: 500,
  paddingLeft: theme.spacing(3),
  paddingRight: theme.spacing(3),
  color:
    theme.palette.mode === "dark" ? "#00c9a7" : theme.palette.secondary.main,
  borderColor: alpha(
    theme.palette.mode === "dark" ? "#00c9a7" : theme.palette.secondary.main,
    0.5
  ),
  "&:hover": {
    borderColor:
      theme.palette.mode === "dark" ? "#00c9a7" : theme.palette.secondary.main,
    backgroundColor: alpha(
      theme.palette.mode === "dark" ? "#00c9a7" : theme.palette.secondary.main,
      0.05
    ),
  },
}));

const StyledSaveButton = styled(Button)(({ theme }) => ({
  borderRadius: "8px",
  textTransform: "none",
  paddingTop: theme.spacing(1),
  paddingBottom: theme.spacing(1),
  paddingLeft: theme.spacing(3),
  paddingRight: theme.spacing(3),
  fontWeight: 500,
  backgroundColor:
    theme.palette.mode === "dark" ? "#00c9a7" : theme.palette.primary.main,
  color: theme.palette.mode === "dark" ? "#000" : "#fff",
  "&:hover": {
    backgroundColor:
      theme.palette.mode === "dark"
        ? alpha("#00c9a7", 0.9)
        : alpha(theme.palette.primary.main, 0.9),
  },
}));

function getTextFieldStyle(theme) {
  return {
    mb: 2,
    "& .MuiOutlinedInput-root": {
      borderRadius: "8px",
      backgroundColor: alpha(theme.palette.background.paper, 0.8),
      "&:hover": {
        boxShadow: "0 0 0 2px rgba(25, 118, 210, 0.1)",
      },
      "&.Mui-focused": {
        boxShadow: "0 0 0 2px rgba(25, 118, 210, 0.2)",
      },
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: alpha(theme.palette.divider, 0.3),
    },
    "& .MuiInputLabel-root": {
      fontSize: "0.875rem",
      fontWeight: 500,
    },
  };
}

export default function LabelEditDialog(props) {
  const { t } = useTranslation("common");
  const theme = useTheme();
  const queryClient = useQueryClient();

  const { open, handleClose, label } = props;
  const isEditMode = !!label?.id;

  const [prevLabelId, setPrevLabelId] = useState(null);

  const [name, setName] = useState("");
  const [color, setColor] = useState(PRESET_COLORS[0]);
  const [customColor, setCustomColor] = useState("");
  const [useCustomColor, setUseCustomColor] = useState(false);
  const [selectedDevices, setSelectedDevices] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [nameError, setNameError] = useState("");

  const selectedOrganisation = useSelector(
    (state) => state.selectedOrganisation
  );

  const { data: allLabels = [] } = useLabels(selectedOrganisation?.id);
  const { data: devices = [] } = useDevices(selectedOrganisation?.id);
  const { data: existingDevices = [] } = useLabelDevices(
    isEditMode ? label.id : null
  );

  const existingNames = useMemo(
    () =>
      new Set(
        allLabels
          .filter((l) => l.id !== label?.id)
          .map((l) => l.name?.toLowerCase().trim())
      ),
    [allLabels, label?.id]
  );

  const activeColor =
    useCustomColor && /^#([0-9A-Fa-f]{3}){1,2}$/.test(customColor)
      ? customColor
      : color;

  const textFieldStyle = getTextFieldStyle(theme);

  // Sync form state when label changes (edit mode)
  const currentLabelId = label?.id ?? null;
  if (open && currentLabelId !== prevLabelId) {
    setPrevLabelId(currentLabelId);
    setName(label?.name || "");
    setNameError("");

    const labelColor = label?.color || PRESET_COLORS[0];
    if (PRESET_COLORS.includes(labelColor)) {
      setColor(labelColor);
      setUseCustomColor(false);
      setCustomColor("");
    } else {
      setCustomColor(labelColor);
      setUseCustomColor(true);
    }

    if (isEditMode && existingDevices.length) {
      setSelectedDevices(
        existingDevices
          .map((d) =>
            devices.find((dev) => dev.id === d.id || dev.id === d.device_id)
          )
          .filter(Boolean)
      );
    } else {
      setSelectedDevices([]);
    }
  }

  // Sync device list when existingDevices loads after dialog opens
  if (
    open &&
    isEditMode &&
    existingDevices.length &&
    selectedDevices.length === 0 &&
    prevLabelId === currentLabelId
  ) {
    const mapped = existingDevices
      .map((d) =>
        devices.find((dev) => dev.id === d.id || dev.id === d.device_id)
      )
      .filter(Boolean);
    if (mapped.length) {
      setSelectedDevices(mapped);
    }
  }

  // Reset when dialog closes
  if (!open && prevLabelId !== null) {
    setPrevLabelId(null);
    setNameError("");
    setUseCustomColor(false);
    setCustomColor("");
  }

  const invalidateLabels = () => {
    queryClient.invalidateQueries({ queryKey: labelKeys.lists() });
    if (isEditMode) {
      queryClient.invalidateQueries({
        queryKey: labelKeys.devices(label.id),
      });
    }
  };

  const handleNameChange = (e) => {
    const value = e.target.value;
    setName(value);
    if (value.trim() && existingNames.has(value.toLowerCase().trim())) {
      setNameError(t("labels.name_already_exists"));
    } else {
      setNameError("");
    }
  };

  const isValidHex = (hex) => /^#([0-9A-Fa-f]{3}){1,2}$/.test(hex);

  const handleSave = async () => {
    if (!name.trim()) {
      UtilityService.updateSnackbarMessage(
        t("common.error_name_required"),
        "error"
      );
      return;
    }

    if (nameError) {
      return;
    }

    const finalColor = useCustomColor
      ? isValidHex(customColor)
        ? customColor
        : PRESET_COLORS[0]
      : color;

    setIsSaving(true);
    try {
      if (isEditMode) {
        const result = await LabelsService.editLabel(label.id, {
          name,
          color: finalColor,
          device_ids: selectedDevices.map((d) => d.id),
        });

        if (result) {
          UtilityService.updateSnackbarMessage(
            t("common.label_updated_successfully")
          );
          invalidateLabels();
          handleClose();
        } else {
          UtilityService.updateSnackbarMessage(
            t("common.error_label_not_updated"),
            "error"
          );
        }
      } else {
        const created = await LabelsService.createLabel(
          name,
          finalColor,
          selectedOrganisation?.id,
          selectedDevices.map((d) => d.id)
        );

        if (created) {
          UtilityService.updateSnackbarMessage(
            t("common.label_created_successfully")
          );
          invalidateLabels();
          handleClose();
        } else {
          UtilityService.updateSnackbarMessage(
            t("common.error_label_not_created"),
            "error"
          );
        }
      }
    } catch (err) {
      if (err?.status === 409) {
        setNameError(t("labels.name_already_exists"));
        UtilityService.updateSnackbarMessage(
          t("labels.name_already_exists"),
          "error"
        );
      } else {
        UtilityService.updateSnackbarMessage(
          isEditMode
            ? t("common.error_label_not_updated")
            : t("common.error_label_not_created"),
          "error"
        );
      }
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <DialogBase
      open={open}
      title={isEditMode ? t("labels.edit") : t("labels.add")}
      handleClose={handleClose}
      icon={
        isEditMode ? (
          <LabelOutlined fontSize="medium" />
        ) : (
          <Add fontSize="medium" />
        )
      }
      actions={
        <>
          <StyledCancelButton variant="outlined" onClick={handleClose}>
            {t("common.cancel")}
          </StyledCancelButton>

          <StyledSaveButton
            variant="contained"
            onClick={handleSave}
            disabled={isSaving}
          >
            {isSaving
              ? t("common.saving")
              : isEditMode
                ? t("common.save")
                : t("common.create")}
          </StyledSaveButton>
        </>
      }
    >
      <Stack spacing={2}>
        <TextField
          id="name"
          label={t("common.name")}
          type="text"
          fullWidth
          value={name}
          onChange={handleNameChange}
          sx={textFieldStyle}
          placeholder={t("labels.enter_name")}
          error={!!nameError}
          helperText={nameError}
        />

        <Box>
          <Typography
            variant="subtitle2"
            sx={{
              mb: 1.5,
              fontWeight: 600,
              color: alpha(theme.palette.text.primary, 0.8),
            }}
          >
            {t("labels.color")}
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {PRESET_COLORS.map((preset) => (
              <Box
                key={preset}
                onClick={() => {
                  setColor(preset);
                  setCustomColor(preset);
                  setUseCustomColor(false);
                }}
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: "8px",
                  backgroundColor: preset,
                  cursor: "pointer",
                  border:
                    !useCustomColor && color === preset
                      ? `3px solid ${theme.palette.text.primary}`
                      : "3px solid transparent",
                  transition: "border-color 0.15s, transform 0.15s",
                  "&:hover": {
                    transform: "scale(1.15)",
                  },
                }}
              />
            ))}
          </Box>

          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 2 }}>
            <TextField
              size="small"
              placeholder="#A1B2C3"
              value={customColor}
              onChange={(e) => {
                let val = e.target.value;
                if (val && !val.startsWith("#")) {
                  val = "#" + val;
                }
                setCustomColor(val.slice(0, 7));
                setUseCustomColor(true);
              }}
              onFocus={() => setUseCustomColor(true)}
              sx={{
                width: 140,
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                },
              }}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <Box
                        component="input"
                        type="color"
                        value={customColor || "#000000"}
                        onChange={(e) => {
                          setCustomColor(e.target.value);
                          setUseCustomColor(true);
                        }}
                        sx={{
                          width: 22,
                          height: 22,
                          padding: 0,
                          border: "none",
                          borderRadius: "4px",
                          cursor: "pointer",
                          backgroundColor: "transparent",
                          "&::-webkit-color-swatch-wrapper": { padding: 0 },
                          "&::-webkit-color-swatch": {
                            border: "none",
                            borderRadius: "4px",
                          },
                          "&::-moz-color-swatch": {
                            border: "none",
                            borderRadius: "4px",
                          },
                        }}
                      />
                    </InputAdornment>
                  ),
                },
              }}
            />
            <Typography variant="caption" color="text.secondary">
              {t("labels.custom_color")}
            </Typography>
          </Stack>
          {activeColor && (
            <Chip
              label={name || t("labels.preview")}
              sx={{
                mt: 2,
                backgroundColor: alpha(activeColor, 0.15),
                color: activeColor,
                fontWeight: 600,
                borderRadius: "16px",
              }}
            />
          )}
        </Box>

        <Divider />

        <Box
          sx={{
            p: 2.5,
            bgcolor: alpha(theme.palette.background.default, 0.4),
            borderRadius: "12px",
            border: `1px solid ${alpha(theme.palette.divider, 0.15)}`,
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{
              mb: 2,
              fontWeight: 600,
              color: alpha(theme.palette.text.primary, 0.8),
            }}
          >
            {t("labels.select_devices")}
          </Typography>

          <Autocomplete
            multiple
            id="device-select"
            options={devices || []}
            value={selectedDevices || []}
            getOptionLabel={(option) => option.name || option.dev_eui || ""}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                placeholder={t("common.select_devices")}
                sx={textFieldStyle}
              />
            )}
            onChange={(event, value) => {
              setSelectedDevices(value);
            }}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  label={option.name || option.dev_eui}
                  {...getTagProps({ index })}
                  key={option.id}
                  sx={{
                    borderRadius: "16px",
                    backgroundColor: alpha(
                      activeColor || theme.palette.primary.main,
                      0.1
                    ),
                    color: activeColor || theme.palette.primary.main,
                    fontWeight: 500,
                  }}
                />
              ))
            }
          />
        </Box>
      </Stack>
    </DialogBase>
  );
}
